import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { StatusBadge } from '@/components/ui/status-badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { ApiError, paymentsApi } from '@/lib/pillarApi';
import {
    PaymentRequestStatus,
    type CustomerPortalDashboardResponse,
    type PaymentRequestListItem,
} from '@pillar-portal/api-types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { formatPaymentRequestSourceDocumentReference } from '@/app/payment-requests/source-document-reference';
import {
    appendCustomerOrganizationUuid,
    getCustomerOrganizationUuidFromSearchParams,
} from '@/app/customer/customer-organization-search';

const PENDING_PAYMENT_REQUEST_STATUSES = [
    PaymentRequestStatus.SENT,
    PaymentRequestStatus.VIEWED,
    PaymentRequestStatus.PARTIALLY_PAID,
    PaymentRequestStatus.REISSUED,
] as const;

function formatAmount(amount: number | undefined, currencyCode: string): string {
    if (amount === undefined) {
        return '—';
    }
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
        }).format(amount);
    } catch {
        return `${amount.toFixed(2)} ${currencyCode}`;
    }
}

function formatDateTime(value: string | undefined): string {
    if (value === undefined) {
        return '—';
    }
    return new Date(value).toLocaleString();
}

export function CustomerPaymentRequestsPageContent() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [selectedMerchantOrganizationUuid, setSelectedMerchantOrganizationUuid] = useState<
        string | undefined
    >(undefined);
    const customerOrganizationUuid: string | undefined = getCustomerOrganizationUuidFromSearchParams(
        searchParams
    );

    const dashboardQuery = useQuery<CustomerPortalDashboardResponse>({
        queryKey: ['customerPortalDashboardFilterOptions'],
        queryFn: async () =>
            await paymentsApi.getCustomerPortalDashboard(
                customerOrganizationUuid === undefined
                    ? {}
                    : { customer_organization_uuid: customerOrganizationUuid }
            ),
        retry: false,
        staleTime: 30000,
    });

    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'customerPendingPaymentRequests',
            `customer=${customerOrganizationUuid ?? 'self'}`,
            `merchant=${selectedMerchantOrganizationUuid ?? 'all'}`,
        ],
        [customerOrganizationUuid, selectedMerchantOrganizationUuid]
    );

    if (dashboardQuery.error !== null) {
        const errorMessage =
            dashboardQuery.error instanceof ApiError
                ? dashboardQuery.error.message
                : 'Failed to load payment-request filters.';
        return (
            <Alert variant="destructive">
                <AlertTitle>Unable to load payment requests</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
        );
    }

    const merchantOptions = dashboardQuery.data?.merchant_options ?? [];

    return (
        <InfiniteScrollList<PaymentRequestListItem, number, ReadonlyArray<string>>
            pageTitle="Pending Payments"
            cardTitle="Pending Payments"
            queryPlaceholder="Search pending payments..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={50}
            fetchPage={async (params) => {
                const response = await paymentsApi.listCustomerPaymentRequests({
                    ...(customerOrganizationUuid === undefined
                        ? {}
                        : { customer_organization_uuid: customerOrganizationUuid }),
                    merchant_organization_uuid: selectedMerchantOrganizationUuid,
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    statuses: [...PENDING_PAYMENT_REQUEST_STATUSES],
                    limit: params.limit,
                    cursor: params.cursor,
                });
                return { items: response.payment_requests, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.number().safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected numeric cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading pending payments...',
                emptyLabel: 'No pending payments found.',
                endOfListLabel: 'End of pending payments.',
                genericErrorLabel: 'Failed to load pending payments.',
            }}
            controls={
                <Select
                    value={selectedMerchantOrganizationUuid ?? 'all'}
                    onValueChange={(value) => {
                        setSelectedMerchantOrganizationUuid(value === 'all' ? undefined : value);
                    }}
                >
                    <SelectTrigger className="w-[240px]">
                        <SelectValue placeholder="Filter by merchant" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All merchants</SelectItem>
                        {merchantOptions.map((merchantOption) => (
                            <SelectItem
                                key={merchantOption.merchant_organization_uuid}
                                value={merchantOption.merchant_organization_uuid}
                            >
                                {merchantOption.merchant_display_name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            }
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((paymentRequest) => {
                        const formattedSourceDocumentReference =
                            formatPaymentRequestSourceDocumentReference({
                                sourceDocumentReference:
                                    paymentRequest.source_document_reference,
                                sourceDocumentKey: paymentRequest.source_document_key,
                            });

                        return (
                            <button
                                key={paymentRequest.payment_request_uuid}
                                type="button"
                                className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                                onClick={() =>
                                    navigate(
                                        appendCustomerOrganizationUuid(
                                            `/customer/payment-requests/${paymentRequest.payment_request_uuid}`,
                                            customerOrganizationUuid
                                        )
                                    )
                                }
                            >
                                <div className="flex items-start justify-between gap-6">
                                    <div className="min-w-0 space-y-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold">
                                                {paymentRequest.title ??
                                                    formattedSourceDocumentReference ??
                                                    'Payment Request'}
                                            </h3>
                                            <StatusBadge status={paymentRequest.status} />
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            Reference:{' '}
                                            {formattedSourceDocumentReference ?? '—'}
                                        </p>
                                    </div>
                                    <div className="space-y-1 text-right text-sm">
                                        <p className="font-semibold">
                                            {formatAmount(
                                                paymentRequest.original_amount,
                                                paymentRequest.currency_code
                                            )}
                                        </p>
                                        <p className="text-muted-foreground">
                                            Created: {formatDateTime(paymentRequest.created_ts)}
                                        </p>
                                        <p className="text-muted-foreground">
                                            Expires: {formatDateTime(paymentRequest.expires_ts)}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        />
    );
}

export default function CustomerPaymentRequestsPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CustomerPaymentRequestsPageContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
